import React, { useContext, useEffect, useState } from "react";
import * as UTILS from "../../utils/Utils";
import { Flex, Menu, message, notification, Spin } from "antd";
import "./toolbar.scss";

import Icon from "@ant-design/icons";
import {
    FlimpLogo,
    IconAdd,
    IconBlocks,
    IconCreate,
    IconCustom,
    IconDesigns,
    IconExit,
    IconExpand,
    IconFile,
    IconInfo,
    IconLaptop,
    IconLibrary,
    IconMobile,
    IconPages,
    IconPreview,
    IconSave,
    IconSmile,
    IconStyle,
    IconTemplates,
    IconTour,
    IconUndo,
    IconVersion,
} from "../../assets/images/toolbar";

import { UserContext } from "../../context/userContext";
import { track } from "@amplitude/analytics-browser";
import PropTypes from "prop-types";

import Style from "../Tool/Style/Style";
import { useSelector } from "react-redux";
import Templates from "../Tool/Layout/Templates/Templates";
import Icons from "../Tool/Layout/Icons/Icons";
import BlocksThumbDND from "../Tool/Layout/Blocks/BlocksThumbDND";
import CustomButtons from "../CustomElements/CustomButtons/CustomButtons";
import { Pages } from "./MenuItems/Pages";
import Library from "../Modal/Library/Library";
import CreateNewDP from "../CreateNewTemplate/CreateNew";
import SaveOrCancel from "../CreateNewTemplate/SaveOrCancel";
import { VersionHistoryContext } from "../../context/versionHistoryContext";
import Info from "../Info/Info";
import { UndoRedo } from "./MenuItems/UndoRedo";
import {
    createSnapshot,
    enterFullscreen,
    exitFullscreen,
    postData,
    postDataForPreview,
    saveTemplate,
} from "./utils";
import { Panel } from "./Panel";
import { AddElements } from "./MenuItems/AddElements";
import ChatbotIcon from "../ui/icons/ChatbotIcon";
import ChatbotSettingsModal from "../CreateNewTemplate/ChatbotSettingsModal";
import ChatbotWidget from "../ui/Chatbot/ChatbotWidget";

const findMenuItemByKey = (key, items) => {
    for (let item of items) {
        if (item.key === key) return item;
        if (item.children) {
            const child = findMenuItemByKey(key, item.children);
            if (child) return child;
        }
    }
    return null;
};

const Logo = () => {
    return (
        <span className="logo-sidebar">
            <FlimpLogo />
        </span>
    );
};

export const Toolbar = ({
    id = 0,
    initP,
    mobileView,
    state,
    handleState,
    onDrag,
    onDrop,
    onDragEnd,
    saveAndExit,
    cancelExit,
    canvasHeightWidth,
    startTour,
    short_code
}) => {
    const { userID, sessionID, flimpID } = useContext(UserContext);
    const { actions: versionHistoryActions, state: versionHistoryState } =
        useContext(VersionHistoryContext);

    const presentState = useSelector((state) => state.present);
    const { layout, layoutList, bgImageId, selectedFont, chatbot } = presentState;

    const [messageApi, messageContext] = message.useMessage();
    const [notificationApi, notificationContext] =
        notification.useNotification();

    const [activeKey, setActiveKey] = useState(null);
    const [activePanel, setActivePanel] = useState(null);
    const [activeModal, setActiveModal] = useState(null);

    const handleClose = () => {
        setActiveKey(null);
        setActiveModal(null);
        setActivePanel(null);
    };

    // SAVE AS BLOCK
    useEffect(() => {
        handleState({
            blockActivated: state.categorySelected,
            animateModal: state.categorySelected,
        });
    }, [state.categorySelected]);


    // PREVIEW
    const [clickedPreview, setClickedPreview] = useState(false);

    // SAVE AS FILE
    const [clickedSaveAsFile, setClickedSaveAsFile] = useState(false);
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const [savingSnapshotError, setSavingSnapshotError] = useState(false);

    const [openKeys, setOpenKeys] = useState([]);
    const [forceOpenSave, setForceOpenSave] = useState(false);

    // Chatbot states
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);

    const onOpenChange = (keys) => {
        if (keys.includes("Save")) {
            setForceOpenSave(true);
        }
        if (!forceOpenSave || keys.includes("Save")) {
            setOpenKeys(keys);
        }
    };

    const onSelect = (e) => {
        if (e.key === "SaveAsFile") {
            setForceOpenSave(true);
            setOpenKeys(["Save"]);
        }
    };

    const itemsTop = [
        {
            key: "Styles",
            icon: <Icon className="first-step" component={IconStyle} />,
            label: "Style",
            modal: (
                <Style
                    flimpID={flimpID}
                    userID={userID}
                    sessionID={sessionID}
                    newChangeDetected={(value) => {
                        handleState({ newChangeDetected: value });
                    }}
                    closeModal={handleClose}
                    googleFonts={UTILS.googleFonts}
                    bgImageId={bgImageId}
                    initP={0}
                    selectedFont={selectedFont}
                />
            ),
        },
        {
            key: "Designs",
            icon: <Icon className="second-step" component={IconDesigns} />,
            label: "Designs",
            children: [
                {
                    key: "1",
                    label: "Templates",
                    icon: <Icon component={IconTemplates} />,
                    onClick: () => {
                        track("Template Option Selected", {
                            entryPoint: "Navigation Bar",
                        });
                    },
                    modal: (
                        <Templates
                            userID={userID}
                            sessionID={sessionID}
                            closeTemplatesPopup={handleClose}
                        />
                    ),
                },
                {
                    key: "2",
                    label: "Blocks",
                    icon: <Icon component={IconBlocks} />,
                    onClick: () => {
                        track("Blocks Option Selected", {
                            entryPoint: "Navigation Bar",
                        });
                    },
                    panel: (
                        <Panel>
                            <BlocksThumbDND
                                ID={id}
                                userID={userID}
                                sessionID={sessionID}
                                closeBlocksSidebar={handleClose}
                                moduleDragStarted={(value) => {
                                    handleState({ moduleDragStarted: value });
                                }}
                                preventMainDrop={(value) => {
                                    handleState({
                                        preventMainDrop: value,
                                    });
                                }}
                            />
                        </Panel>
                    ),
                },
                {
                    key: "3",
                    label: "Icons",
                    icon: <Icon component={IconSmile} />,
                    onClick: () => {
                        track("Icons Option Selected", {
                            entryPoint: "Navigation Bar",
                        });
                    },
                    panel: (
                        <Panel>
                            <Icons onDrag={onDrag} close={handleClose} />
                        </Panel>
                    ),
                },
            ],
        },
        {
            key: "Custom Components",
            icon: <Icon className="fifteenth-step" component={IconCustom} />,
            label: "Custom elements",
            panel: (
                <Panel onClose={handleClose}>
                    <CustomButtons
                        selectedFont={selectedFont}
                        onDrag={onDrag}
                    />
                </Panel>
            ),
        },
        {
            key: "Add Element",
            icon: <Icon className="third-step" component={IconAdd} />,
            label: "Add elements",
            panel: (
                <Panel onClose={handleClose}>
                    <AddElements onDrag={onDrag} />
                </Panel>
            ),
        },
        {
            key: "Pages",
            icon: <Icon className="fourth-step" component={IconPages} />,
            label: "Pages",
            panel: (
                <Panel onClose={handleClose}>
                    <Pages handleState={handleState} />
                </Panel>
            ),
        },
        {
            key: "Library",
            icon: <Icon className="fifth-step" component={IconLibrary} />,
            label: "My library",
            modal: (
                <Library
                    userID={userID}
                    sessionID={sessionID}
                    videoDataID={state.videoDataID}
                    modalUpload={state.modalUpload}
                    toggleUploadPopup={handleClose}
                    initP={initP}
                    clickedImageID={null}
                    videoClicked={null}
                    fileType={null}
                />
            ),
        },
        {
            key: "Preview",
            icon: clickedPreview ? (
                <Spin size="small" />
            ) : (
                <Icon className="sixth-step" component={IconPreview} />
            ),
            label: "Preview",
            onClick: () => {
                setClickedPreview(true);
                handleState({
                    errorInSave: false,
                    nothingToSave: true,
                });
                postDataForPreview(
                    {
                        presentState,
                        layout,
                        layoutList,
                        short_code: short_code,
                    },
                    flimpID,
                    userID,
                    sessionID
                )
                    .then(() => {
                        handleState({
                            saved: true,
                            errorInSave: false,
                            nothingToSave: false,
                        });
                    })
                    .catch(() => {
                        handleState({
                            errorInSave: true,
                            saved: false,
                            nothingToSave: false,
                        });
                    })
                    .finally(() => {
                        setClickedPreview(false);
                    });
            },
        },
        {
            key: "Save",
            icon: <Icon className="seventh-step" component={IconSave} />,
            label: "Save",
            onMouseLeave: () => {
                setForceOpenSave(false);
            },
            children: [
                {
                    key: "SaveAsBlock",
                    label: "Save as Block",
                    icon: <Icon component={IconBlocks} />,
                    onClick: () => {
                        setForceOpenSave(false);
                        setOpenKeys([]);
                        handleState({
                            categoryValue: "custom",
                            categorySelected: true,
                            choosenBlockElement: [],
                        });
                    },
                },
                {
                    key: "SaveAsTemplate",
                    label: "Save as Template",
                    icon: !state.templateLoader ? (
                        <Icon component={IconTemplates} />
                    ) : (
                        <Spin size="small" />
                    ),
                    onClick: () => {
                        handleState({
                            templateLoader: true,
                        });
                        messageApi.open({
                            type: "loading",
                            content: "Saving template...",
                            duration: 0,
                        });
                        saveTemplate(
                            {
                                presentState,
                                layout,
                                blockHeight: state.blockHeight,
                            },
                            userID,
                            sessionID
                        )
                            .then(() => {
                                notificationApi["success"]({
                                    message: "Save as template",
                                    description:
                                        "The template has been saved successfully",
                                    placement: "bottomRight",
                                });
                                messageApi.destroy();
                                handleState({
                                    templateSaved: true,
                                    templateLoader: false,
                                });
                            })
                            .catch(() => {
                                notificationApi["error"]({
                                    message: "Save as template",
                                    description: "Failed to save template",
                                    placement: "bottomRight",
                                });
                                messageApi.destroy();
                                handleState({
                                    templateSaved: false,
                                    templateLoader: false,
                                });
                            });
                    },
                },
                {
                    key: "SaveAsFile",
                    label: !clickedSaveAsFile ? (
                        "Save as File"
                    ) : savingSnapshotError ? (
                        <div
                            style={{
                                lineHeight: 1,
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                flexDirection: "column",
                            }}
                        >
                            <span style={{ color: "green" }}>
                                Data is stored successfully,
                            </span>
                            <span style={{ color: "red" }}>
                                Failed to generate snapshot
                            </span>
                        </div>
                    ) : savingSnapshot ? (
                        "Creating snapshot"
                    ) : (
                        "Saving file"
                    ),
                    icon: !clickedSaveAsFile ? (
                        <Icon component={IconFile} />
                    ) : savingSnapshotError ? (
                        <Icon component={IconUndo} />
                    ) : (
                        <Spin size="small" />
                    ),
                    onClick: () => {
                        setForceOpenSave(true);
                        setClickedSaveAsFile(true);
                        setSavingSnapshotError(false);
                        setSavingSnapshot(false);

                        messageApi.open({
                            type: "loading",
                            content: "Saving file...",
                            duration: 0,
                        });
                        postData(presentState, flimpID, userID, sessionID).then(
                            () => {
                                const portalHeight =
                                    canvasHeightWidth.clientHeight;
                                const portalWidth = presentState.percentage
                                    ? 1200
                                    : canvasHeightWidth.clientWidth;
                                handleState({
                                    portalHeight,
                                    portalWidth,
                                });
                                setSavingSnapshot(true);

                                return createSnapshot(
                                    portalHeight,
                                    portalWidth,
                                    flimpID,
                                    userID,
                                    sessionID
                                )
                                    .then(() => {
                                        setSavingSnapshotError(false);
                                        setClickedSaveAsFile(false);
                                        notificationApi["success"]({
                                            message: "Save as file",
                                            description:
                                                "The file has been saved successfully",
                                            placement: "bottomRight",
                                        });
                                    })
                                    .catch(() => {
                                        setSavingSnapshotError(true);
                                        notificationApi["info"]({
                                            message: "Save as file",
                                            description: (
                                                <>
                                                    <span
                                                        style={{
                                                            color: "green",
                                                        }}
                                                    >
                                                        Data is stored
                                                        successfully,
                                                    </span>
                                                    <br />
                                                    <span
                                                        style={{ color: "red" }}
                                                    >
                                                        Failed to generate
                                                        snapshot
                                                    </span>
                                                </>
                                            ),
                                            placement: "bottomRight",
                                        });
                                    })
                                    .finally(() => {
                                        messageApi.destroy();
                                        setSavingSnapshot(false);
                                    });
                            }
                        );
                    },
                },
            ],
        },
        {
            key: "Expand",
            icon: <Icon className="eighth-step" component={IconExpand} />,
            label: "Expand",
            onClick: () => {
                if (state.fullScreen) {
                    exitFullscreen();
                } else {
                    enterFullscreen();
                }
                handleState({ fullScreen: !state.fullScreen });
            },
        },
        {
            key: "Create New",
            icon: <Icon className="twelth-step" component={IconCreate} />,
            label: "Create new",
            modal: (
                <CreateNewDP
                    cancelNewCard={handleClose}
                    setCardTitle={(e) => {
                        handleState({ newCardTitle: e.target.value });
                    }}
                    newCardTitle={state.titleError}
                    limitExceed={state.limitExceed}
                    newCardDscr={state.dscrError}
                    setCardDescription={(e) => {
                        handleState({ newCardDescription: e.target.value });
                    }}
                />
            ),
        },
        {
            key: "Version Tool",
            icon: <Icon className="fourteenth-step" component={IconVersion} />,
            label: "Version history",
            onClick: () => {
                versionHistoryActions.openVersionHistory();
                handleState({ mobileView: false });
            },
        },
        // Conditionally include Chatbot item based on localStorage
        ...(localStorage.getItem("chatbot") == "true" ? [{
            key: "Chatbot",
            icon: <Icon component={ChatbotIcon} />,
            label: "Chatbot",
            modal: (
                <ChatbotSettingsModal
                    closeModal={handleClose}
                />
            )
        }] : []),
        {
            key: "Save Exit",
            icon: <Icon className="thirteenth-step" component={IconExit} />,
            label: "Exit",
            modal: (
                <SaveOrCancel
                    saved={state.saved}
                    saveAndExit={saveAndExit}
                    cancel={cancelExit}
                    close={handleClose}
                />
            ),
        },
    ];

    const itemsBottom = [
        {
            key: "Device View",
            icon: (
                <Flex className="device-view-items ninth-step">
                    <div className={mobileView ? "active" : ""}>
                        <IconMobile />
                    </div>
                    <div className={mobileView ? "" : "active"}>
                        <IconLaptop />
                    </div>
                </Flex>
            ),
            label: "Device view",
            onClick: () => {
                handleState({ mobileView: !mobileView });
            },
        },
        {
            key: "undoredo",
            icon: <UndoRedo />,
            label: "Undo / Redo",
        },
        {
            key: "tour",
            icon: <Icon component={IconTour} />,
            label: "Tour",
            onClick: () => {
                startTour();
            },
        },
        {
            key: "info",
            icon: <Icon className="sixteenth-step" component={IconInfo} />,
            label: "Info",
            modal: (
                <Info
                    userID={userID}
                    sessionID={sessionID}
                    flimpID={flimpID}
                    closeModal={handleClose}
                />
            ),
        },
    ];

    // HANDLE CLICK MENU ITEM
    const onClick = (e) => {
        setActiveKey((currentKey) => {
            if (currentKey === e.key) {
                handleClose();
                return null;
            } else {
                const clickedItem =
                    findMenuItemByKey(e.key, [...itemsTop, ...itemsBottom]) ||
                    {};
                handleTrackNavigation(clickedItem.key);
                setActivePanel(clickedItem.panel || null);
                setActiveModal(clickedItem.modal || null);

                if (clickedItem.panel || clickedItem.modal) {
                    return e.key;
                } else return null;
            }
        });
    };

    const handleTrackNavigation = (key) => {
        track("Navigation Item", {
            menuItem: key,
            deviceView: mobileView ? "Mobile" : "Desktop",
        });
    };

    if (!versionHistoryState.showHistory)
        return (
            <Flex>
                <Flex vertical justify="space-between" className="main-sidebar">
                    <Flex vertical>
                        <Logo />
                        <Menu
                            onClick={onClick}
                            mode="inline"
                            items={itemsTop}
                            className="menu-top toolbar-container"
                            inlineCollapsed={true}
                            selectedKeys={[activeKey]}
                            openKeys={openKeys}
                            onOpenChange={onOpenChange}
                            onSelect={onSelect}
                        />
                    </Flex>
                    <Menu
                        onClick={onClick}
                        mode="inline"
                        items={itemsBottom}
                        className="menu-bottom toolbar-container"
                        inlineCollapsed={true}
                        selectedKeys={[activeKey]}
                    />
                </Flex>
                {messageContext}
                {notificationContext}
                {activeKey && activePanel}
                {activeKey && activeModal}

                {/* Chatbot Widget */}
                {chatbot.display && chatbot.url && (
                    <ChatbotWidget
                        isOpen={isChatbotOpen}
                        onClose={() => setIsChatbotOpen(false)}
                        onToggle={() => setIsChatbotOpen(!isChatbotOpen)}
                        chatbotUrl={chatbot.url}
                        buttonColor={chatbot.buttonColor || "#1A202C"}
                        buttonTextColor={chatbot.buttonTextColor || "#ffffff"}
                        buttonText={chatbot.buttonText || "Chat with us"}
                    />
                )}
            </Flex>
        );
};

Toolbar.propTypes = {
    id: PropTypes.number,
    initP: PropTypes.number,
    mobileView: PropTypes.bool,
    state: PropTypes.object,
    handleState: PropTypes.func,
    onDrag: PropTypes.func,
    onDrop: PropTypes.func,
    onDragEnd: PropTypes.func,
    saveAndExit: PropTypes.func,
    cancelExit: PropTypes.func,
    canvasHeightWidth: PropTypes.any,
};
